import {
  TEMPLATE_DECISION_CLASSES,
  TEMPLATE_SOURCE_NONE,
  type TemplateListItemDto,
  type TemplateSectionDto,
  type TemplateWaitDto,
} from '@pams/shared';
import type { TemplateState } from '@/lib/labels';
import { templateStateOf } from '@/lib/statusPresentation';
import { LIBRARY_COPY } from '../copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1 §2 · §3c · §3e] THE LIBRARY TREE, AS DATA — pure, so a test reads it without a render.
 *
 *  Part → section → division → template, as the booklet prints them and as the review site the
 *  policing team verified lays them out. Three things are DERIVED here rather than written down:
 *
 *    • the DIVISION ORDER — the order divisions first appear in the set. Measured on the 2025 booklet,
 *      it is exactly the review site's hand-written list (North, Central, South, South East, …), so a
 *      list in code would be a second copy of a fact the data already states, and next year's booklet
 *      orders itself;
 *    • which columns are UNUSUAL — used by no more than `TEMPLATE_RARE_COLUMN_MAX` templates;
 *    • each row's LINE OF WORDS — what it waits on, what it produced, whether its columns are unusual.
 *
 *  ⚠️ THE STATE IS THE SERVER'S (`templateStateOf` over `readiness` and `produced`), and `waitsOn` only
 *  explains it: a blocked row with nothing open says the first reason the server gave instead.
 *
 *  [amended TEMPLATE-FIND] The filter reads a template NUMBER exactly (`templateKeyQueryOf`), and the one
 *  template a filter leaves (`onlyTemplateOf`) is what Enter opens.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [P7B.1 §3c] A COLUMN NO MORE THAN THIS MANY TEMPLATES USE IS UNUSUAL. Measured on the 2025 booklet:
 * thirteen columns, used between them by 17 of the 182 templates; the next labels up are women-police
 * ranks used by 6 and 7, which already have post types. Usage is counted over each template's `force`,
 * so round-the-clock duty points — never built — count for nothing.
 */
export const TEMPLATE_RARE_COLUMN_MAX = 5;

export const STATE_FILTERS = ['ALL', 'BLOCKED', 'READY', 'CREATED', 'NOT_BUILT'] as const;
export type StateFilter = (typeof STATE_FILTERS)[number];

export const COLUMN_FILTERS = ['ALL', 'USUAL', 'UNUSUAL'] as const;
export type ColumnFilter = (typeof COLUMN_FILTERS)[number];

export type TreeFilters = { readonly text: string; readonly state: StateFilter; readonly columns: ColumnFilter };
export const NO_FILTERS: TreeFilters = { text: '', state: 'ALL', columns: 'ALL' };

export const isFiltering = (filters: TreeFilters): boolean =>
  filters.text.trim() !== '' || filters.state !== 'ALL' || filters.columns !== 'ALL';

/** Round the clock and an empty template are both "not built" in the filter, told apart by their words. */
export const stateFilterOf = (state: TemplateState): Exclude<StateFilter, 'ALL'> =>
  state === 'ROUND_THE_CLOCK' || state === 'EMPTY' ? 'NOT_BUILT' : state;

/** Each unusual column and how many templates use it. */
export const rareColumnsOf = (items: readonly TemplateListItemDto[]): Map<string, number> => {
  const usage = new Map<string, Set<string>>();
  for (const item of items) {
    for (const total of item.force) {
      const users = usage.get(total.column) ?? new Set<string>();
      users.add(item.id);
      usage.set(total.column, users);
    }
  }
  return new Map(
    [...usage]
      .filter(([, users]) => users.size <= TEMPLATE_RARE_COLUMN_MAX)
      .map(([column, users]) => [column, users.size]),
  );
};

/** The templates using an unusual column, by id, with the unusual columns each uses in its own order. */
export const unusualTemplatesOf = (
  items: readonly TemplateListItemDto[],
  rare: ReadonlyMap<string, number>,
): Map<string, string[]> =>
  new Map(
    items.flatMap((item) => {
      const columns = [...new Set(item.force.map((total) => total.column))].filter((column) => rare.has(column));
      return columns.length === 0 ? [] : [[item.id, columns] as const];
    }),
  );

/** Divisions in the order they first appear in the set; a template with no division sorts last. */
export const divisionOrderOf = (items: readonly TemplateListItemDto[]): Map<string, number> => {
  const order = new Map<string, number>();
  for (const item of [...items].sort((a, b) => a.ordinal - b.ordinal)) {
    if (item.divisionCode !== null && !order.has(item.divisionCode)) order.set(item.divisionCode, order.size);
  }
  return order;
};

/** The key a Part's own header section carries — `i` for Part I, the import's slug of the Part's id. */
export const partHeaderKeyOf = (part: string): string =>
  part
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** A section's code as the booklet prints it — `I-A` for `i-a`. */
export const sectionCodeOf = (key: string): string => key.toUpperCase();

export type TreeTemplate = {
  readonly item: TemplateListItemDto;
  readonly state: TemplateState;
  readonly unusual: boolean;
};
export type TreeDivision = { readonly code: string | null; readonly name: string | null; templates: TreeTemplate[] };
export type TreeSection = {
  readonly key: string;
  readonly title: string;
  readonly code: string;
  readonly divisions: TreeDivision[];
  readonly count: number;
};
export type TreePart = {
  readonly part: string;
  /** The Part's own title — its header section's, which the booklet prints as the Part's heading. */
  readonly title: string;
  readonly sections: TreeSection[];
  /** Templates shown under this Part with the filters applied. */
  readonly count: number;
  /** Templates in this Part. */
  readonly total: number;
};
export type Tree = { readonly parts: TreePart[]; readonly shown: number; readonly total: number };

const textOf = (item: TemplateListItemDto): string =>
  [item.title, item.key, item.sectionKey, item.sectionTitle, item.divisionName ?? '', item.divisionCode ?? '']
    .join(' ')
    .toLowerCase();

/** Everything a person may search but the number — what a NUMBER query still matches as a substring. */
const wordsOf = (item: TemplateListItemDto): string =>
  [item.title, item.sectionKey, item.sectionTitle, item.divisionName ?? '', item.divisionCode ?? '']
    .join(' ')
    .toLowerCase();

/**
 * ⚠️ [TEMPLATE-FIND] TYPED TEXT THAT IS A TEMPLATE NUMBER — `t152`, `T152`, `152`, ` 152 `, `t0152` —
 * is that template's key, `t152`; anything else, `0` included, is `null`. The Decisions page, the
 * record page and a person reading the booklet all name a template this way, and the library index
 * reads `?t=` through the same function, so `?t=152` and a typed `152` find the same template.
 */
export const templateKeyQueryOf = (text: string): string | null => {
  const digits = /^\s*t?0*(\d+)\s*$/i.exec(text)?.[1];
  /* `0*` is greedy, so the digits never carry a leading zero — except `0` itself, which is no template. */
  return digits === undefined || digits === '0' ? null : `t${digits}`;
};

/**
 * ⚠️ [TEMPLATE-FIND] A NUMBER MATCHES ITS TEMPLATE EXACTLY, NEVER AS A SUBSTRING OF ANOTHER KEY — `t1` is
 * not t10–t19, and `t6` is not t60–t69 — while the words still match as before, so `41` finds t41 AND
 * "41 Pally M. G. Road". Any other text is substring over every field, the key included, as it always was.
 */
export const matchesFilters = (entry: TreeTemplate, filters: TreeFilters): boolean => {
  const text = filters.text.trim().toLowerCase();
  const key = templateKeyQueryOf(text);
  if (key !== null) {
    if (entry.item.key !== key && !wordsOf(entry.item).includes(text)) return false;
  } else if (text !== '' && !textOf(entry.item).includes(text)) return false;
  if (filters.state !== 'ALL' && stateFilterOf(entry.state) !== filters.state) return false;
  if (filters.columns === 'USUAL' && entry.unusual) return false;
  if (filters.columns === 'UNUSUAL' && !entry.unusual) return false;
  return true;
};

/** A Part's own heading as the booklet prints it — its header section's title — or `null` when it has none. */
export const partHeaderTitleOf = (part: string, sections: readonly TemplateSectionDto[]): string | null =>
  sections.find((section) => section.part === part && section.key === partHeaderKeyOf(part))?.title ?? null;

/** A Part's title: its header section's, else the bare "Part X". */
export const partTitleOf = (part: string, sections: readonly TemplateSectionDto[]): string =>
  partHeaderTitleOf(part, sections) ?? LIBRARY_COPY.part(part);

/** "Part I: Police arrangements, …" — or just "Part II" when the booklet prints no heading for it. */
export const partHeadingOf = (part: string, sections: readonly TemplateSectionDto[]): string => {
  const title = partHeaderTitleOf(part, sections);
  return title === null ? LIBRARY_COPY.part(part) : `${LIBRARY_COPY.part(part)}: ${title}`;
};

/**
 * The tree, in booklet order: Parts and sections as the set lists them, templates under their section
 * grouped by division in the set's own division order, each group in booklet order. A Part's HEADER
 * section is the Part itself — it opens the Part pane — and is not listed again as a section. A
 * template whose section the set does not list (it should not happen) still appears, under its Part.
 */
export const buildTree = (
  sections: readonly TemplateSectionDto[],
  items: readonly TemplateListItemDto[],
  filters: TreeFilters,
  unusual: ReadonlySet<string>,
): Tree => {
  const order = divisionOrderOf(items);
  const filtering = isFiltering(filters);
  const bySection = new Map<string, TreeTemplate[]>();
  for (const item of [...items].sort((a, b) => a.ordinal - b.ordinal)) {
    const entries = bySection.get(item.sectionKey) ?? [];
    entries.push({ item, state: templateStateOf(item), unusual: unusual.has(item.id) });
    bySection.set(item.sectionKey, entries);
  }
  const listed = new Set(sections.map((section) => section.key));
  const known: Array<{ key: string; part: string; title: string }> = [
    ...sections.map((section) => ({ key: section.key, part: section.part, title: section.title })),
    ...items
      .filter((item) => !listed.has(item.sectionKey))
      .filter((item, index, all) => all.findIndex((other) => other.sectionKey === item.sectionKey) === index)
      .map((item) => ({ key: item.sectionKey, part: item.part, title: item.sectionTitle })),
  ];
  const partOrder = [...new Set(known.map((section) => section.part))];
  const parts = partOrder.map((part): TreePart => {
    const ofPart = known.filter((section) => section.part === part);
    const all = ofPart.flatMap((section) => bySection.get(section.key) ?? []);
    const treeSections = ofPart
      .filter((section) => section.key !== partHeaderKeyOf(part) || (bySection.get(section.key) ?? []).length > 0)
      .map((section): TreeSection => {
        const shown = (bySection.get(section.key) ?? []).filter((entry) => matchesFilters(entry, filters));
        const divisions: TreeDivision[] = [];
        for (const entry of [...shown].sort(
          (a, b) =>
            (a.item.divisionCode === null ? Infinity : (order.get(a.item.divisionCode) ?? Infinity)) -
              (b.item.divisionCode === null ? Infinity : (order.get(b.item.divisionCode) ?? Infinity)) ||
            a.item.ordinal - b.item.ordinal,
        )) {
          const last = divisions.at(-1);
          if (last && last.code === entry.item.divisionCode) last.templates.push(entry);
          else divisions.push({ code: entry.item.divisionCode, name: entry.item.divisionName, templates: [entry] });
        }
        return {
          key: section.key,
          title: section.title,
          code: sectionCodeOf(section.key),
          divisions,
          count: shown.length,
        };
      })
      .filter((section) => !filtering || section.count > 0);
    return {
      part,
      title: partTitleOf(part, sections),
      sections: treeSections,
      count: treeSections.reduce((sum, section) => sum + section.count, 0),
      total: all.length,
    };
  });
  const shownParts = filtering ? parts.filter((part) => part.count > 0) : parts;
  return {
    parts: shownParts,
    shown: shownParts.reduce((sum, part) => sum + part.count, 0),
    total: items.length,
  };
};

/**
 * ⚠️ [TEMPLATE-FIND] THE ONE TEMPLATE THE TREE SHOWS, OR `null` — what Enter in the filter opens. Walked
 * over the tree as drawn, so it agrees with the rows on screen and with the count above them.
 */
export const onlyTemplateOf = (tree: Tree): TreeTemplate | null => {
  const shown = tree.parts.flatMap((part) =>
    part.sections.flatMap((section) => section.divisions.flatMap((division) => division.templates)),
  );
  const [only] = shown;
  return shown.length === 1 && only !== undefined ? only : null;
};

// ── The row's line of words ([P7B.1 §3e]) ──────────────────────────────────────────

/** A wait in a few words: what to decide, never the whole decision — the panel and the pane say the rest. */
export const waitWordOf = (wait: TemplateWaitDto): string => {
  switch (wait.class) {
    case 'SOURCE':
      return wait.code === TEMPLATE_SOURCE_NONE ? LIBRARY_COPY.waitWord.blankSource : wait.label;
    case 'FORCE_COLUMN':
      return wait.label;
    case 'POLICY':
      return LIBRARY_COPY.waitWord.policy;
    case 'IN_CHARGE_ROLE':
      return LIBRARY_COPY.waitWord.inCharge;
    case 'TOTALS':
      return LIBRARY_COPY.waitWord.totals;
    case 'SCHEDULE':
      return LIBRARY_COPY.waitWord.schedule;
    case 'DUTY_POINT':
      return LIBRARY_COPY.waitWord.dutyPoint;
  }
};

/** Distinct words in class order — a policy asked and a policy waiting are one "a policy". */
export const waitWordsOf = (waits: readonly TemplateWaitDto[]): string[] => [
  ...new Set(
    [...waits]
      .sort((a, b) => TEMPLATE_DECISION_CLASSES.indexOf(a.class) - TEMPLATE_DECISION_CLASSES.indexOf(b.class))
      .map(waitWordOf),
  ),
];

/**
 * ⚠️ [P7B.1 §3e] WHY A ROW IS IN THE STATE IT IS, IN WORDS, ON THE ROW ITSELF: what a blocked template
 * waits on — or, with nothing open, the first reason the server gave — the order a created one
 * produced, and whether it uses an unusual column. Empty for a ready template with nothing to add:
 * its dot says it.
 */
export const rowLineOf = (entry: TreeTemplate): string => {
  const parts: string[] = [];
  const live = entry.item.produced.filter((order) => order.archivedAt === null);
  const [first] = live;
  if (first) parts.push(LIBRARY_COPY.produced(first.paNumber ?? first.reference, live.length - 1));
  if (entry.item.readiness.state === 'BLOCKED') {
    const words = waitWordsOf(entry.item.waitsOn);
    const reason = entry.item.readiness.reasons[0];
    if (words.length > 0) parts.push(LIBRARY_COPY.waitsOn(words));
    else if (reason !== undefined) parts.push(reason);
  }
  if (entry.unusual) parts.push(LIBRARY_COPY.unusualColumn);
  return parts.join(' · ');
};
